/**
 * siliconSkinPipeline — translucent silicone surface, pressed-on deformation.
 *
 * A raymarched SDF sphere with bass-driven displacement creates the
 * sensation of fingers pushing into a translucent silicone pad. Subsurface
 * scattering uses a one-tap back-light estimate, micro skin pores come
 * from a high-frequency fbm, and chromatic dispersion splits the
 * refracted ray by wavelength.
 *
 * Audio: bass drives the deformation amplitude; mid drives chromatic
 * separation; treble drives subsurface sparkle.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface SiliconSkinPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;
uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash13(vec3 p) {
  p = fract(p * 0.1031);
  p += dot(p, p.yzx + 33.33);
  return fract((p.x + p.y) * p.z);
}

float noise3(vec3 p) {
  vec3 i = floor(p);
  vec3 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float n000 = hash13(i);
  float n100 = hash13(i + vec3(1, 0, 0));
  float n010 = hash13(i + vec3(0, 1, 0));
  float n110 = hash13(i + vec3(1, 1, 0));
  float n001 = hash13(i + vec3(0, 0, 1));
  float n101 = hash13(i + vec3(1, 0, 1));
  float n011 = hash13(i + vec3(0, 1, 1));
  float n111 = hash13(i + vec3(1, 1, 1));
  return mix(
    mix(mix(n000, n100, f.x), mix(n010, n110, f.x), f.y),
    mix(mix(n001, n101, f.x), mix(n011, n111, f.x), f.y),
    f.z
  );
}

float fbm3(vec3 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * noise3(p);
    p = p * 2.07 + vec3(1.3, -2.7, 0.9);
    a *= 0.5;
  }
  return v;
}

float sdSphere(vec3 p, float r) { return length(p) - r; }

// Bass-driven "finger press" deformation. Multiple animated emitters push
// into the surface from the +Z direction.
float displace(vec3 p) {
  float t = uTime;
  float bassAmp = (uBass.x + uBass.y + uBass.z) / 3.0;
  bassAmp = pow(clamp(bassAmp * 2.2, 0.0, 1.0), 0.7);
  float d = 0.0;
  // Five finger-prints orbit slowly.
  for (int i = 0; i < 5; i++) {
    float fi = float(i);
    float ph = fi * 1.2566;
    vec2 c = 0.55 * vec2(
      cos(t * (0.31 + 0.07 * fi) + ph),
      sin(t * (0.27 + 0.05 * fi) - ph * 0.5)
    );
    float r2 = dot(p.xy - c, p.xy - c);
    float fall = exp(-r2 * 9.0);
    float press = bassAmp * (0.32 + 0.18 * sin(t * (0.9 + fi * 0.3) + fi));
    d -= press * fall;
  }
  // Skin micro-detail — micro pores etched across the surface.
  d += (fbm3(p * 18.0 + vec3(0.0, t * 0.5, 0.0)) - 0.5) * 0.012;
  // Mid-frequency ridges that bulge and relax.
  d += sin(p.x * 8.0 + t * 1.3) * cos(p.y * 7.0 - t * 0.9) * 0.018
     * (0.4 + (uMid.x + uMid.y) * 0.6);
  return d;
}

float mapSilicone(vec3 p) {
  float base = sdSphere(p, 1.0);
  return base + displace(p);
}

vec3 calcNormal(vec3 p) {
  vec2 e = vec2(0.0015, 0.0);
  return normalize(vec3(
    mapSilicone(p + e.xyy) - mapSilicone(p - e.xyy),
    mapSilicone(p + e.yxy) - mapSilicone(p - e.yxy),
    mapSilicone(p + e.yyx) - mapSilicone(p - e.yyx)
  ));
}

// Cheap subsurface scattering — sample interior density along the back ray.
float sss(vec3 p, vec3 n) {
  vec3 q = p - n * 0.22;
  float a = 0.0;
  for (int i = 0; i < 6; i++) {
    float fi = float(i);
    a += fbm3(q * (3.0 + fi * 0.4) + vec3(uTime * 0.3)) * (0.6 - fi * 0.08);
  }
  return clamp(a, 0.0, 1.0);
}

void main() {
  vec2 uv = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  uv.x *= uResolution.x / uResolution.y;

  // Camera floats back; mid audio sways it slightly.
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.2, 0.0, 1.0), 0.65);
  mid = pow(clamp(mid * 2.9, 0.0, 1.0), 0.75);
  high = pow(clamp(high * 3.5, 0.0, 1.0), 0.85);

  vec3 ro = vec3(0.0, 0.0, 3.2 + sin(uTime * 0.13) * 0.08);
  ro.xy += vec2(sin(uTime * 0.21), cos(uTime * 0.17)) * 0.04 * mid;
  vec3 rd = normalize(vec3(uv, -1.6));

  // Chromatic dispersion: each channel samples a slightly different ray.
  vec3 col = vec3(0.0);
  float dispersion = 0.004 + mid * 0.018;
  for (int ch = 0; ch < 3; ch++) {
    float fc = float(ch) - 1.0; // -1, 0, +1
    vec3 rdc = normalize(vec3(uv * (1.0 + fc * dispersion), -1.6));

    // Raymarch.
    float t = 0.0;
    bool hit = false;
    vec3 p = ro;
    for (int i = 0; i < 80; i++) {
      float d = mapSilicone(p);
      if (d < 0.0008) { hit = true; break; }
      if (t > 7.0) break;
      t += d * 0.85;
      p = ro + rdc * t;
    }
    vec3 c = vec3(0.018, 0.025, 0.045); // dark blue base
    if (hit) {
      vec3 n = calcNormal(p);
      vec3 ldir = normalize(vec3(0.4, 0.6, 0.7));
      float diff = clamp(dot(n, ldir), 0.0, 1.0);
      float fres = pow(1.0 - clamp(dot(n, -rdc), 0.0, 1.0), 3.0);

      // Subsurface light.
      float sub = sss(p, n);
      vec3 sssCol = mix(vec3(0.95, 0.55, 0.7), vec3(0.45, 0.85, 1.0), 0.5 + 0.5 * sin(uTime * 0.4));

      // Specular — skin oil sheen.
      vec3 h = normalize(ldir - rdc);
      float spec = pow(clamp(dot(n, h), 0.0, 1.0), 64.0);

      c = vec3(0.0);
      c += diff * vec3(0.85, 0.78, 0.85);
      c += fres * vec3(0.6, 0.75, 1.0) * (0.6 + high * 1.4);
      c += sub * sssCol * (0.55 + bass * 0.6);
      c += spec * vec3(1.0) * (0.4 + bass * 0.8);
    }
    col[ch] = c[ch];
  }

  // Vignette.
  float vig = pow(16.0 * vUV.x * (1.0 - vUV.x) * vUV.y * (1.0 - vUV.y), 0.25);
  col *= vig;

  // Tone map + gamma.
  col = col / (1.0 + col);
  col = pow(col, vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}`;

export function computeSiliconSkinBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.4 * Math.sin(t * 0.9);
  out[1] = 0.6 + 0.4 * Math.abs(Math.sin(t * 1.7));
  out[2] = 0.4 + 0.3 * Math.sin(t * 2.5);
  out[3] = 0.5 + 0.4 * Math.sin(t * 1.1 + 1.0);
  out[4] = 0.5 + 0.4 * Math.sin(t * 1.7 + 2.3);
  out[5] = 0.4 + 0.4 * Math.sin(t * 2.5 + 3.1);
  out[6] = 0.3 + 0.5 * Math.sin(t * 4.0 + 0.5);
  out[7] = 0.4 + 0.4 * Math.sin(t * 6.0 + 1.7);
  out[8] = 0.3 + 0.5 * Math.sin(t * 8.0 + 2.1);
}

export function computeSiliconSkinRealtimeBands(freqData: Uint8Array, out: Float32Array): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.008], [0.008, 0.030], [0.030, 0.060],
    [0.060, 0.150], [0.150, 0.260], [0.260, 0.420],
    [0.420, 0.620], [0.620, 0.800], [0.800, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    let s = 0, c = 0;
    const a = Math.floor(lo * N);
    const b = Math.floor(hi * N);
    for (let k = a; k < b; k++) { s += freqData[k]; c++; }
    out[i] = c > 0 ? (s / c) / 255 : 0;
  }
}

export function createSiliconSkinPipeline(gl: WebGL2RenderingContext): SiliconSkinPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawSiliconSkin(
  gl: WebGL2RenderingContext,
  pipe: SiliconSkinPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}